// src/components/CarouselTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import '../styles.css';

const CarouselTab = ({ selectedTopic }) => {
  const [images, setImages] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [thumbnailUrls, setThumbnailUrls] = useState([]);
  const [message, setMessage] = useState('');

  const backendURL = process.env.REACT_APP_BACKEND_URL;

  const fetchImages = async () => {
    try {
      setMessage('Fetching images...');
      const response = await axios.post(`${backendURL}/list_images`, {
        topic: selectedTopic,
      });

      if (response.status === 200) {
        setImages(response.data.images);
        setMessage('Images fetched successfully.');
        if (response.data.images.length > 0) {
          fetchImage(response.data.images[0]);
          fetchThumbnails(response.data.images);
        }
      } else {
        setMessage('Failed to fetch images.');
      }
    } catch (error) {
      console.error(error);
      setMessage('Error fetching images.');
    }
  };

  const fetchImage = async (filename) => {
    try {
      const response = await axios.post(`${backendURL}/get_image`, {
        filename,
        topic: selectedTopic,
      }, { responseType: 'blob' });

      if (response.status === 200) {
        const imageUrl = URL.createObjectURL(response.data);
        setCurrentImageUrl(imageUrl);
        copyImageToClipboard(response.data);
      } else {
        setMessage('Failed to load image.');
      }
    } catch (error) {
      console.error('Error loading image:', error);
      setMessage('Error loading image.');
    }
  };

  const fetchThumbnails = async (imageList) => {
    try {
      const urls = await Promise.all(imageList.map(async (filename) => {
        const response = await axios.post(`${backendURL}/get_image`, {
          filename,
          topic: selectedTopic,
        }, { responseType: 'blob' });

        if (response.status === 200) {
          return URL.createObjectURL(response.data);
        } else {
          console.error('Failed to load thumbnail:', filename);
          return '';
        }
      }));
      setThumbnailUrls(urls);
    } catch (error) {
      console.error('Error loading thumbnails:', error);
    }
  };

  const copyImageToClipboard = async (blob) => {
    try {
      const clipboardItem = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([clipboardItem]);
      setMessage('Image copied to clipboard.');
    } catch (error) {
      console.error('Failed to copy image:', error);
      setMessage('Failed to copy image.');
    }
  };

  const handlePrev = useCallback(() => {
    const newIndex = currentIdx === 0 ? images.length - 1 : currentIdx - 1;
    setCurrentIdx(newIndex);
    fetchImage(images[newIndex]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, images]);

  const handleNext = useCallback(() => {
    const newIndex = currentIdx === images.length - 1 ? 0 : currentIdx + 1;
    setCurrentIdx(newIndex);
    fetchImage(images[newIndex]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, images]);

  const handleThumbnailClick = (index) => {
    setCurrentIdx(index);
    fetchImage(images[index]);
  };

  const downloadAllImages = async () => {
    try {
      const response = await axios.get(`${backendURL}/get_all_images/${selectedTopic}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedTopic}_images.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading images:", error);
      setMessage("Error downloading images.");
    }
  };

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'ArrowLeft') {
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    },
    [handlePrev, handleNext]
  );

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopic]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
        Carousel
      </h2>

      {images.length > 0 ? (
        <>
          <div className="relative">
            <img
              src={currentImageUrl}
              alt={`Carousel ${currentIdx + 1}`}
              className="w-full object-contain rounded-md shadow-md transition-transform duration-300 ease-in-out"
            />
            <button
              onClick={handlePrev}
              className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-white dark:bg-gray-800 bg-opacity-50 dark:bg-opacity-50 p-2 rounded-full hover:bg-opacity-75 dark:hover:bg-opacity-75 focus:outline-none"
            >
              <ChevronLeftIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
            <button
              onClick={handleNext}
              className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-white dark:bg-gray-800 bg-opacity-50 dark:bg-opacity-50 p-2 rounded-full hover:bg-opacity-75 dark:hover:bg-opacity-75 focus:outline-none"
            >
              <ChevronRightIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
          </div>

          <div className="flex overflow-x-auto mt-4 space-x-2 pb-4">
            {thumbnailUrls.map((url, index) => (
              <div key={images[index]} className="flex flex-col items-center">
                <div className={`w-20 h-20 rounded-md cursor-pointer ${index !== currentIdx ? 'unselected-thumbnail' : ''}`} onClick={() => handleThumbnailClick(index)}>
                  <img
                    src={url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs mt-1 text-center text-gray-700 dark:text-gray-300">
                  {images[index]}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center text-gray-700 dark:text-gray-300">
          No images available.
        </div>
      )}

      {message && (
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          {message}
        </div>
      )}

      <button
        onClick={downloadAllImages}
        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
      >
        Download All Images
      </button>
    </div>
  );
};

export default CarouselTab;
